import BaseModel from '@core/model';

import NotificationTemplateTypeEnum from '@constants/enums/notification/templateType';
import { CommercialTierEnum, AdviserCommercialTierEnum } from '@constants/enums/organisation/commercialTier';
import OrganisationStatusEnum from '@constants/enums/organisation/status';
import OrganisationTypeEnum from '@constants/enums/organisation/type';
import PlatformFeatureEnum from '@constants/enums/platform/feature';

import i18n from '@plugins/i18n';

import { User, CompanyInfo, Address, PreviousCompanyName, PhoneNumber } from '@/models';

export class Organisation extends BaseModel {
    static entity = 'organisations';
    static runSetup = true;

    static fields() {
        return {
            ...super.fields(),
            id: this.attr(null),
            title: this.string(null).nullable(),
            name: this.string(null).nullable(),
            status: this.enum(OrganisationStatusEnum),
            type: this.enum(OrganisationTypeEnum).nullable(),
            gi_ref: this.string(null).nullable(),
            fca_number: this.string(null).nullable(),
            salesforce_id: this.string(null).nullable(),
            salesforce_last_sync: this.string(null).nullable(),
            addresses: this.modelList(Address, null).nullable(),
            previous_company_names: this.modelList(PreviousCompanyName, null).nullable(),
            company_info: this.model(CompanyInfo, null).nullable(),
            key_people: this.modelList(User, null).nullable(),
            verticals: this.attr(null),
            annual_stats: this.attr(null),
            users: this.hasMany(User, 'organisation_id'),
            phone_numbers: this.modelList(PhoneNumber, null).nullable(),
            primary_user_id: this.string(null).nullable(),
            primary_user: this.belongsTo(User, 'primary_user_id'),
            created_at: this.string(null).nullable(),
            last_updated: this.string(null).nullable(),
            notifications: this.attr(null).nullable(),
            root: this.boolean(false).nullable(),
            commercial_tier: this.enum(CommercialTierEnum, null).nullable(),
            is_configured_for_sso: this.boolean(false).nullable(),
            hide_demo_data: this.boolean(false).nullable(),
            features: this.enumList(PlatformFeatureEnum, [])
        };
    }

    setupLogic() {
        if (this.commercial_tier === null) {
            this.commercial_tier = this.default_commercial_tier;
        }
        if (this.is_root) {
            this.features = Object.values(PlatformFeatureEnum);
        }
    }

    get full_name() {
        let name = [];

        if (this.title) {
            name.push(this.title);
        }

        if (this.name) {
            name.push(this.name);
        }

        return name.join(' ');
    }

    get is_unverified() {
        return this.status === OrganisationStatusEnum.UNVERIFIED;
    }

    get is_active() {
        return this.status === OrganisationStatusEnum.ACTIVE;
    }

    get is_suspended() {
        return this.status === OrganisationStatusEnum.SUSPENDED;
    }

    get is_inactive() {
        return this.status === OrganisationStatusEnum.INACTIVE;
    }

    get is_root() {
        return this.is_adviser && this.root === true;
    }
    get is_adviser() {
        return this.type === OrganisationTypeEnum.ADVISER;
    }
    get is_investor() {
        return this.type === OrganisationTypeEnum.INVESTOR;
    }
    get is_broker() {
        return this.type === OrganisationTypeEnum.BROKER;
    }
    get is_company() {
        return this.type === OrganisationTypeEnum.COMPANY;
    }
    get is_custodian() {
        return this.type === OrganisationTypeEnum.CUSTODIAN;
    }
    get is_fund_manager() {
        return this.type === OrganisationTypeEnum.FUND_MANAGER;
    }
    get is_nominee() {
        return this.type === OrganisationTypeEnum.NOMINEE;
    }
    get is_provider() {
        return this.type === OrganisationTypeEnum.PRODUCT_PROVIDER;
    }
    get is_receiving_agent() {
        return this.type === OrganisationTypeEnum.RECEIVINGAGENT;
    }
    get is_registrar() {
        return this.type === OrganisationTypeEnum.REGISTRAR;
    }

    get phone_number() {
        if (!this.phone_numbers || this.phone_numbers.length === 0) {
            return null;
        }

        const phone_number = this.phone_numbers[0];

        try {
            if (phone_number instanceof PhoneNumber) {
                return phone_number;
            } else {
                return new PhoneNumber({
                    e164: phone_number.number.e164,
                    number: phone_number.number.national_number,
                    country_code: phone_number.number.region,
                    type: phone_number.number_type
                });
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    get has_invalid_phone_number() {
        if (!Array.isArray(this.phone_numbers)) {
            return false;
        }

        for (const pn of this.phone_numbers) {
            if (!pn.$deleted && !pn.is_valid) {
                return true;
            }
        }

        return false;
    }

    get company_phone_number() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.phone_number;
    }

    set company_phone_number(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.phone_number = value;
    }

    get company_email() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.email;
    }

    set company_email(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.email = value;
    }

    get website() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.website;
    }

    set website(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.website = value;
    }

    get linkedin_url() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.linkedin_url;
    }

    set linkedin_url(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.linkedin_url = value;
    }

    get twitter_user() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.twitter_user;
    }

    set twitter_user(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.twitter_user = value;
    }

    get twitter_url() {
        if (!this.twitter_user) {
            return null;
        }

        return `${i18n.t('external_urls.twitter')}/${this.twitter_user}`;
    }

    get company_number() {
        if (!this.company_info) {
            return null;
        }
        return this.company_info.company_number;
    }

    set company_number(value) {
        if (!this.company_info) {
            this.company_info = new CompanyInfo();
        }
        this.company_info.company_number = value;
    }

    get companieshouse_url() {
        if (!this.company_number) {
            return null;
        }

        return `${i18n.t('external_urls.companieshouse')}/${this.company_number}`;
    }

    get address() {
        let address = (this.addresses || []).find(address => address.default);

        if (!address) {
            return null;
        }

        if (!(address instanceof Address)) {
            address = new Address(address);
        }

        return address.computed_name;
    }

    get status_icon() {
        if (this.status === OrganisationStatusEnum.UNVERIFIED) {
            return 'dotCircle';
        } else if (this.status === OrganisationStatusEnum.ACTIVE) {
            return 'circle';
        } else if (this.status === OrganisationStatusEnum.SUSPENDED) {
            return 'circle';
        } else if (this.status === OrganisationStatusEnum.INACTIVE) {
            return 'dotCircle';
        }

        return 'batteryHalf';
    }

    get status_icon_colour() {
        if (this.status === OrganisationStatusEnum.UNVERIFIED) {
            return 'warning';
        } else if (this.status === OrganisationStatusEnum.ACTIVE) {
            return 'success';
        } else if (this.status === OrganisationStatusEnum.SUSPENDED) {
            return 'error';
        } else if (this.status === OrganisationStatusEnum.INACTIVE) {
            return 'grey';
        }

        return 'grey';
    }

    get status_text_colour() {
        if (this.status === OrganisationStatusEnum.UNVERIFIED) {
            return 'warning--text text--darken-1';
        } else if (this.status === OrganisationStatusEnum.ACTIVE) {
            return 'success--text';
        } else if (this.status === OrganisationStatusEnum.SUSPENDED) {
            return 'error--text text--darken-1';
        } else if (this.status === OrganisationStatusEnum.INACTIVE) {
            return 'grey--text';
        }

        return 'warning--text text--darken-1';
    }

    get unread_notifications_count() {
        return this.notifications?.unread || 0;
    }

    async $getNotificationPreferencesTemplate(notificationTemplateType = NotificationTemplateTypeEnum.USERS) {
        return (await this.constructor.api.getNotificationPreferencesTemplate(this.id, notificationTemplateType)).data;
    }

    async $updateNotificationPreferencesTemplate(notificationTemplateType, template) {
        return (
            await this.constructor.api.updateNotificationPreferencesTemplate(
                this.id,
                notificationTemplateType,
                template
            )
        ).data;
    }

    get commercial_tier_enum() {
        switch (this.type) {
            case OrganisationTypeEnum.ADVISER:
                return AdviserCommercialTierEnum;
            default:
                return CommercialTierEnum;
        }
    }

    get default_commercial_tier() {
        switch (this.type) {
            case OrganisationTypeEnum.ADVISER:
                return AdviserCommercialTierEnum[1];
            default:
                return CommercialTierEnum[0];
        }
    }

    get commercial_tier_level() {
        const tierValue = this.commercial_tier === null ? this.default_commercial_tier : this.commercial_tier;
        const tierKeys = Object.keys(CommercialTierEnum);
        const tierLevel = tierKeys.find(key => CommercialTierEnum[`${key}`] === tierValue);
        return tierLevel ? parseFloat(tierLevel) : 0;
    }

    hasTierAccess(level = 0) {
        return this.commercial_tier_level >= level;
    }

    get commercial_tier_enum_a11y_key() {
        switch (this.type) {
            case OrganisationTypeEnum.ADVISER:
                return 'enums.adviser_commercial_tier';
            case OrganisationTypeEnum.FUND_MANAGER:
                return 'enums.fund_manager_commercial_tier';
            case OrganisationTypeEnum.PRODUCT_PROVIDER:
                return 'enums.provider_commercial_tier';
            default:
                return 'enums.commercial_tier';
        }
    }

    get commercial_tier_enum_a11y_description_key() {
        switch (this.type) {
            case OrganisationTypeEnum.ADVISER:
                return 'enums.adviser_commercial_tier_description';
            case OrganisationTypeEnum.FUND_MANAGER:
                return 'enums.fund_manager_commercial_tier_description';
            case OrganisationTypeEnum.PRODUCT_PROVIDER:
                return 'enums.provider_commercial_tier_description';
            default:
                return null;
        }
    }

    get scope_id() {
        if (this.is_investor) {
            if (this.Auth().is_gi) {
                return null;
            }
            return this.adviser_accounts.find(Boolean)?.adviser_id || null;
        }

        return this.id;
    }

    get default_settings_tab() {
        return 'Settings';
    }

    hasFeature(feature) {
        if (!Array.isArray(this.features)) {
            return false;
        }

        return this.features.includes(feature);
    }

    get has_feature_basic_visibility_groups() {
        return this.hasFeature(PlatformFeatureEnum.BASIC_VISIBILITY_GROUPS);
    }

    get has_feature_advanced_visibility_groups() {
        return this.hasFeature(PlatformFeatureEnum.ADVANCED_VISIBILITY_GROUPS);
    }
}

export default Organisation;
